import { useEffect, useMemo, useRef, useState } from 'react'
import Input from '../../ui/input/input'
import DefaultModal from '../defaultModal/defaultModal'
import Button from '../../ui/button/button'
import html2canvas from 'html2canvas'
import { jsPDF } from 'jspdf'
import { getDeclination } from '../../../helpers/common'

const Sticker = ({ company, title, logo, code, quantityPerBox }) => {
  return (
    <div className='mb-[5rem] text-center text-Regular(24_30) p-[1.2rem] flex flex-col items-center gap-[0.8rem] bg-white max-w-[40rem] m-auto'>
      <span>{company}</span>
      <span className='text-Regular(18_24)'>
        <span>{title} - </span>
        <span className='font-bold'>{code}</span>
      </span>
      <span>{logo}</span>
      {quantityPerBox && <span>{quantityPerBox} штук</span>}
    </div>
  )
}

function StickerModal({ stickerModal, setStickerModal }) {
  const fullBoxTemplateRef = useRef(null)
  const remainderTemplateRef = useRef(null)
  const [form, setForm] = useState({})
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const waitForNextFrame = () =>
    new Promise(resolve => {
      requestAnimationFrame(() => resolve())
    })

  useEffect(() => {
    setForm({
      company: stickerModal.company || '',
      logo: stickerModal.logo || '',
      orderQuantity: stickerModal.quantity,
      code: stickerModal.code || '',
    })
  }, [stickerModal])

  const estimated = useMemo(() => {
    const orderQuantity = Number(form.orderQuantity)
    const quantityPerBox = Number(form.quantityPerBox)

    if (!orderQuantity || !quantityPerBox) {
      return {
        total: 0,
        remainderItems: 0,
        remainderBox: 0,
        boxQuantities: []
      }
    }

    const total = Math.floor(orderQuantity / quantityPerBox)
    const remainderItems = orderQuantity % quantityPerBox
    const remainderBox = remainderItems > 0 ? 1 : 0
    const boxQuantities = [
      ...new Array(total).fill(quantityPerBox),
      ...(remainderItems > 0 ? [remainderItems] : [])
    ]

    return {
      total,
      remainderItems,
      remainderBox,
      boxQuantities
    }
  }, [form.orderQuantity, form.quantityPerBox])

  const onInputHandler = ({ prop, val }) => {
    const value = val.target.value
    setForm(prev => ({
      ...prev,
      [prop]: value
    }))
  }

  const onPdfExport = async () => {
    const totalPages = estimated.total + estimated.remainderBox
    if (!totalPages) return

    setIsGeneratingPdf(true)
    await waitForNextFrame()

    let pdf

    try {
      const buildTemplateImage = async templateRef => {
        const template = templateRef?.current
        if (!template) return null

        const canvas = await html2canvas(template, {
          scale: 1.5,
          useCORS: true,
          backgroundColor: '#ffffff'
        })

        const width = canvas.width
        const height = canvas.height
        const orientation = width > height ? 'l' : 'p'
        return {
          width,
          height,
          orientation,
          image: canvas.toDataURL('image/jpeg', 0.85)
        }
      }

      const appendPage = templateData => {
        if (!templateData) return
        const { width, height, orientation, image } = templateData

        if (!pdf) {
          pdf = new jsPDF({
            orientation,
            unit: 'px',
            format: [width, height]
          })
        } else {
          pdf.addPage([width, height], orientation)
        }

        pdf.addImage(image, 'JPEG', 0, 0, width, height, undefined, 'FAST')
      }

      const fullBoxTemplate = estimated.total > 0
        ? await buildTemplateImage(fullBoxTemplateRef)
        : null
      const remainderTemplate = estimated.remainderBox > 0
        ? await buildTemplateImage(remainderTemplateRef)
        : null

      for (let index = 0; index < estimated.total; index++) {
        appendPage(fullBoxTemplate)
      }

      if (estimated.remainderBox > 0) {
        appendPage(remainderTemplate)
      }

      const stickersWord = getDeclination(totalPages, ['стикер', 'стикера', 'стикеров'])
      const fileName = `${form.company} - ${stickerModal.code} (${totalPages} ${stickersWord}).pdf`
      pdf.save(fileName)
    } catch (error) {
      console.error('onPdfExport error: ', error)
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  return (
    <div className='w-[90vw] max-w-[76rem] min-h-[10rem]'>
      <DefaultModal title='Создать стикер'>
        <div className='py-[5rem] px-[4rem] bg-Dominant/Dop '>
          <div className='text-Regular(16_18) mb-[5rem]'>
            <span>{stickerModal.title} - </span>
            <span className='font-bold'>{stickerModal.code}</span>
          </div>
          <div className='grid grid-cols-2 gap-[2.6rem] mb-[5rem]'>
            <Input
              onInput={val => onInputHandler({ prop: 'company', val })}
              initial={stickerModal.company}
              border={false}
              label='Компания'></Input>
            <Input
              onInput={val => onInputHandler({ prop: 'logo', val })}
              border={false}
              initial={stickerModal.logo}
              label='Название логотипа'></Input>
            <Input
              border={false}
              initial={stickerModal.quantity}
              label='Штук в заказе'></Input>
            <Input
              onInput={val => onInputHandler({ prop: 'quantityPerBox', val })}
              border={false}
              type='number'
              maxValue={stickerModal.quantity}
              // initial={form.quantityPerBox || ''}
              label='Штук в коробке'></Input>
          </div>
          <Sticker
            logo={form.logo}
            company={form.company}
            title={stickerModal.title}
            quantityPerBox={form.quantityPerBox}
            code={stickerModal.code}></Sticker>
          <div className='mb-[2.4rem]'>
            {isGeneratingPdf && (
              <div className='flex flex-col gap-[0.8rem]'>
                <div className='text-Regular(16_18)'>
                  Генерация PDF...
                </div>
                <div className='h-[0.8rem] w-full bg-white/70 rounded-[0.4rem] overflow-hidden'>
                  <div
                    className='h-full w-[35%] bg-Accent/Blue'
                    style={{
                      animation: 'sticker-progress-slide 1.2s linear infinite alternate',
                      willChange: 'transform'
                    }}></div>
                </div>
              </div>
            )}
          </div>
          <div className='flex justify-between'>
            {form.quantityPerBox && (
              <div className='flex flex-col gap-[0.4rem] text-Regular(16_18)'>
                <span>
                  <b>{estimated.total}</b> коробки по <b>{form.quantityPerBox}</b> шт
                </span>
                {estimated.remainderItems > 0 && (
                  <span>
                    <b>{estimated.remainderBox}</b> коробка по <b>{estimated.remainderItems}</b> шт
                  </span>
                )}
              </div>
            )}
            <div className='ml-auto'>
              <Button
                disabled={!form.quantityPerBox || isGeneratingPdf}
                name={isGeneratingPdf ? 'Генерация...' : 'Создать'}
                onPress={onPdfExport}></Button>
            </div>
          </div>
        </div>
        <style>
          {`
            @keyframes sticker-progress-slide {
              0% {
                transform: translateX(-100%);
              }
              100% {
                transform: translateX(190%);
              }
            }
          `}
        </style>
        <div className='fixed left-[-10000px] top-0 pointer-events-none opacity-0'>
          {Number(form.quantityPerBox) > 0 && (
            <div className='sticker-to-print' ref={fullBoxTemplateRef}>
              <Sticker
                logo={form.logo}
                company={form.company}
                title={stickerModal.title}
                code={stickerModal.code}
                quantityPerBox={Number(form.quantityPerBox)}></Sticker>
            </div>
          )}
          {estimated.remainderItems > 0 && (
            <div className='sticker-to-print' ref={remainderTemplateRef}>
              <Sticker
                logo={form.logo}
                company={form.company}
                title={stickerModal.title}
                code={stickerModal.code}
                quantityPerBox={estimated.remainderItems}></Sticker>
            </div>
          )}
        </div>
      </DefaultModal>
    </div>
  )
}

export default StickerModal
